import React, { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const MLModels = () => {
  const [modelStatus, setModelStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('status');
  const [previewPredictions, setPreviewPredictions] = useState([]);
  const { isAdmin } = useAuth();
  const userIsAdmin = isAdmin();
  const [showAdminTools, setShowAdminTools] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [previewFetched, setPreviewFetched] = useState(false);

  const apiJsonRequest = async (endpoint, options = {}) => {
    const response = await apiRequest(endpoint, options);
    let payload = null;
    try {
      payload = await response.json();
    } catch (err) {
      payload = null;
    }

    if (!response.ok) {
      const message =
        payload?.detail ||
        payload?.message ||
        payload?.error ||
        `Request failed with status ${response.status}`;
      throw new Error(message);
    }

    return payload;
  };

  const fetchModelStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await apiJsonRequest('/ml/model-status');
      setModelStatus(
        data && typeof data === 'object'
          ? data
          : { model_status: {}, models_trained: false }
      );
    } catch (err) {
      console.error('Error fetching model status:', err);
      setError('Failed to fetch model status. The ML models endpoint may not be available.');
      // Set a safe default structure
      setModelStatus({ model_status: {}, models_trained: false });
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchPredictionPreview = useCallback(async () => {
    try {
      setIsPreviewLoading(true);
      setPreviewError(null);
      const data = await apiJsonRequest('/ml/prediction-preview?limit=10');
      if (data.success) {
        setPreviewPredictions(data.predictions || []);
        setPreviewFetched(true);
      } else {
        setPreviewPredictions([]);
        setPreviewError(data.message || 'Unable to load predictions');
      }
    } catch (err) {
      setPreviewPredictions([]);
      setPreviewError(err.message || 'Unable to load predictions');
    } finally {
      setIsPreviewLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModelStatus();
  }, [fetchModelStatus]);

  useEffect(() => {
    if (activeTab === 'prediction' && !previewFetched) {
      fetchPredictionPreview();
    }
  }, [activeTab, previewFetched, fetchPredictionPreview]);

  const trainModel = async (modelType) => {
    setIsLoading(true);
    setError(null);
    
    try {
      let endpoint = '';
      switch (modelType) {
        case 'performance':
          endpoint = '/ml/train-performance-model';
          break;
        case 'clustering':
          endpoint = '/ml/train-clustering-model';
          break;
        case 'anomaly':
          endpoint = '/ml/train-anomaly-detection';
          break;
        case 'recommendations':
          endpoint = '/ml/build-recommendations';
          break;
        default:
          throw new Error('Invalid model type');
      }

      const result = await apiJsonRequest(endpoint, { method: 'POST' });
      
      if (result.success) {
        alert(result.message || `${modelType} model trained successfully!`);
        fetchModelStatus();
      } else {
        setError(result.error || result.message || `Failed to train ${modelType} model`);
      }
    } catch (err) {
      setError(`Failed to train ${modelType} model: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const renderModelStatus = () => {
    if (!modelStatus) return <div>Loading model status...</div>;

    const { model_status = {}, models_trained, training_metadata } = modelStatus || {};

    // Check if model_status exists and is an object
    if (!model_status || typeof model_status !== 'object') {
      return (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          <p>Model status data is not available. The ML models endpoint may not be configured.</p>
        </div>
      );
    }

    if (!userIsAdmin) {
      return (
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold mb-2">Admin tools unavailable</h3>
          <p className="text-sm text-gray-600">
            Only admins can view model health and trigger retraining. Please contact your admin if you
            need these controls. Prediction insights remain available under the Performance tab.
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold">Admin Tools</h3>
              <p className="text-sm text-gray-500">
                Manage training artifacts and monitor model health. Visible to admins only.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold uppercase text-gray-500 bg-gray-100 px-2 py-1 rounded">
                Admin only
              </span>
              <button
                onClick={() => setShowAdminTools(prev => !prev)}
                className="border border-gray-300 px-4 py-2 rounded hover:bg-gray-50"
              >
                {showAdminTools ? 'Hide Controls' : 'Show Controls'}
              </button>
            </div>
          </div>
        </div>

        {showAdminTools && (
          <>
            {training_metadata && (
              <div className="bg-blue-50 border border-blue-200 text-blue-900 px-6 py-4 rounded">
                <p className="font-semibold">Latest Training Snapshot</p>
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <p>
                    Records Used:{' '}
                    <span className="font-medium">{training_metadata.records_used}</span>
                  </p>
                  <p>
                    Recommended Minimum:{' '}
                    <span className="font-medium">{training_metadata.recommended_minimum}</span>
                  </p>
                  <p>
                    Last Trained:{' '}
                    <span className="font-medium">
                      {training_metadata.trained_at
                        ? new Date(training_metadata.trained_at).toLocaleString()
                        : '—'}
                    </span>
                  </p>
                  <p>
                    Models Ready:{' '}
                    <span className="font-medium">
                      {models_trained ? '✅ Ready' : '⚠️ Needs Training'}
                    </span>
                  </p>
                </div>
                {training_metadata.limited_data && (
                  <p className="mt-3 text-sm text-yellow-800">
                    Models were trained on fewer than {training_metadata.recommended_minimum} campaigns.
                    Add more data for production-grade accuracy.
                  </p>
                )}
              </div>
            )}

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Model Training Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.keys(model_status).length === 0 ? (
                  <div className="col-span-2 text-center text-gray-500 py-8">
                    <p>No model status information available.</p>
                  </div>
                ) : (
                  Object.entries(model_status).map(([modelName, status]) => {
                    const lastUpdated = status.last_modified
                      ? new Date(status.last_modified * 1000).toLocaleString()
                      : '—';
                    const sizeKb = status.size ? (status.size / 1024).toFixed(2) : '0.00';
                    return (
                      <div key={modelName} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium capitalize">
                            {modelName.replace(/_/g, ' ')}
                          </span>
                          <span className={`px-2 py-1 rounded text-sm ${
                            status.exists ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {status.exists ? 'Trained' : 'Not Trained'}
                          </span>
                        </div>
                        {status.exists && (
                          <div className="text-sm text-gray-600">
                            <p>Size: {sizeKb} KB</p>
                            <p>Last Modified: {lastUpdated}</p>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Training Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                  onClick={() => trainModel('performance')}
                  disabled={isLoading}
                  className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  {isLoading ? 'Training...' : 'Train Performance Model'}
                </button>
                <button
                  onClick={() => trainModel('clustering')}
                  disabled={isLoading}
                  className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600 disabled:opacity-50"
                >
                  {isLoading ? 'Training...' : 'Train Clustering Model'}
                </button>
                <button
                  onClick={() => trainModel('anomaly')}
                  disabled={isLoading}
                  className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 disabled:opacity-50"
                >
                  {isLoading ? 'Training...' : 'Train Anomaly Detection'}
                </button>
                <button
                  onClick={() => trainModel('recommendations')}
                  disabled={isLoading}
                  className="bg-purple-500 text-white px-4 py-2 rounded hover:bg-purple-600 disabled:opacity-50"
                >
                  {isLoading ? 'Building...' : 'Build Recommendations'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderPerformancePrediction = () => {
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold">Latest Campaign Predictions</h3>
              <p className="text-sm text-gray-500">
                Auto-generated insights using your most recent campaign data.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={fetchPredictionPreview}
                disabled={isPreviewLoading}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {isPreviewLoading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {previewError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
              {previewError}
            </div>
          )}

          {isPreviewLoading && previewPredictions.length === 0 ? (
            <div className="text-center text-gray-500 py-8">Loading predictions...</div>
          ) : previewPredictions.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No campaign data available yet. Add campaigns to see automatic predictions.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Campaign
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Spend
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Predicted TCS
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Predicted ER
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actual ER
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Confidence
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewPredictions.map(item => (
                    <tr key={item.campaign_id}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900">{item.influencer_name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500">
                          {item.product || 'General'} • {item.platform || 'Unknown'}
                        </p>
                        <p className="text-xs text-gray-400">{item.date || 'No date'}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        ₹{(item.amount || 0).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                        {item.predicted_tcs_score?.toFixed(2) ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600">
                        {item.predicted_engagement_rate !== undefined
                          ? `${(item.predicted_engagement_rate * 100).toFixed(2)}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.actual_engagement_rate !== undefined
                          ? `${(item.actual_engagement_rate * 100).toFixed(2)}%`
                          : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {item.confidence || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">ML Models Dashboard</h1>
        <p className="text-gray-600 mt-2">
          Train and use machine learning models for campaign insights
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('status')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'status'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Model Status
            </button>
            <button
              onClick={() => setActiveTab('prediction')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'prediction'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Performance Prediction
            </button>
          </nav>
        </div>
      </div>

      {activeTab === 'status' && renderModelStatus()}
      {activeTab === 'prediction' && renderPerformancePrediction()}
    </div>
  );
};

export default MLModels;
